import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, AlertTriangle, Settings2, Plus, Trash2, X, Pencil, Briefcase } from "lucide-react";
import { getAlocacaoPessoal, putAlocacaoPessoal, getProjetos } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import ComboBoxFiltravel from "../../componets/ComboBoxFiltravel";
import ProjetosCrudModal from "./ProjetosCrudModal";
import { MESES_ABREV } from "../../utils/orcamento";

const MESES_1_12 = Array.from({ length: 12 }, (_, idx) => idx + 1);

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  bgActive: isLight ? "#EEF2FF" : "#241A3D",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function somaMes(colaborador, mes) {
  return (colaborador.alocacoes_por_mes[mes] || []).reduce((acc, a) => acc + a.pct, 0);
}

function corCelula(C, total) {
  if (total === 100) return { bg: `${C.success}22`, fg: C.success };
  if (total >= 50) return { bg: `${C.warning}22`, fg: C.warning };
  if (total > 0) return { bg: `${C.danger}22`, fg: C.danger };
  return { bg: "transparent", fg: C.muted };
}

export default function AlocacaoPessoalView({ tituloModulo }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [colaboradores, setColaboradores] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [projetosModalAberto, setProjetosModalAberto] = useState(false);
  const [colaboradorAberto, setColaboradorAberto] = useState(null);
  const [colaboradorResumo, setColaboradorResumo] = useState(null);

  async function carregarProjetos() {
    try {
      setProjetos(await getProjetos());
    } catch (e) {
      setErro(e.message);
    }
  }

  async function carregar() {
    setLoading(true);
    setErro("");
    try {
      const [lista] = await Promise.all([getAlocacaoPessoal(ano), carregarProjetos()]);
      setColaboradores(lista || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [ano]);

  const projetosAtivos = useMemo(() => projetos.filter((p) => p.ativo), [projetos]);

  function aoSalvarColaborador(colaboradorId, novasAlocacoesPorMes) {
    setColaboradores((lista) => lista.map((c) => (c.id === colaboradorId ? { ...c, alocacoes_por_mes: novasAlocacoesPorMes } : c)));
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{tituloModulo} · OPERAÇÃO · {ano}</h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>Clique em um colaborador para ver o resumo de projetos alocados; use o ícone de lápis para editar.</p>
              </div>
              <button onClick={() => setProjetosModalAberto(true)} style={btnStyle(C.brand, true)}>
                <Settings2 size={15} /> Gerenciar Projetos
              </button>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : colaboradores.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum colaborador da OPERAÇÃO cadastrado em Custo Mão de Obra para {ano}.
              </div>
            ) : (
              <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ position: "sticky", left: 0, background: C.card, padding: "10px 14px", textAlign: "left", fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}`, minWidth: 220 }}>Colaborador</th>
                      {MESES_ABREV.map((m) => (
                        <th key={m} style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}`, minWidth: 64 }}>{m}</th>
                      ))}
                      <th style={{ padding: "10px 8px", borderBottom: `1px solid ${C.border}`, minWidth: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {colaboradores.map((c) => (
                      <tr key={c.id} onClick={() => setColaboradorResumo(c)} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = C.bgActive; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td style={{ position: "sticky", left: 0, background: "inherit", padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>
                          {c.cargo || "—"}{c.ocupante ? ` · ${c.ocupante}` : " · vaga em aberto"}
                        </td>
                        {MESES_ABREV.map((_, idx) => {
                          const mes = idx + 1;
                          const total = somaMes(c, mes);
                          const cor = corCelula(C, total);
                          return (
                            <td key={mes} style={{ padding: 4, borderBottom: `1px solid ${C.border}`, textAlign: "center" }}>
                              <div style={{ padding: "8px 4px", borderRadius: 8, background: cor.bg, color: cor.fg, fontWeight: 700, fontSize: 12 }}>
                                {total > 0 ? `${total}%` : "—"}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ padding: 4, borderBottom: `1px solid ${C.border}`, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setColaboradorAberto(c)} title="Editar alocação"
                            style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.text }}
                          >
                            <Pencil size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>

      {colaboradorResumo && (
        <ResumoColaboradorModal
          C={C}
          colaborador={colaboradorResumo}
          ano={ano}
          onClose={() => setColaboradorResumo(null)}
          onEditar={() => {
            setColaboradorAberto(colaboradorResumo);
            setColaboradorResumo(null);
          }}
        />
      )}

      {colaboradorAberto && (
        <PopupAlocacaoColaborador
          C={C}
          colaborador={colaboradorAberto}
          ano={ano}
          projetosAtivos={projetosAtivos}
          onClose={() => setColaboradorAberto(null)}
          onSalvo={aoSalvarColaborador}
        />
      )}

      {projetosModalAberto && (
        <ProjetosCrudModal C={C} projetos={projetos} onClose={() => setProjetosModalAberto(false)} onProjetosChange={carregarProjetos} />
      )}
    </div>
  );
}

function linhasIniciais(colaborador) {
  const mapa = new Map();
  MESES_1_12.forEach((mes) => {
    (colaborador.alocacoes_por_mes[mes] || []).forEach((a) => {
      if (!mapa.has(a.projeto_id)) {
        mapa.set(a.projeto_id, {
          projeto_id: a.projeto_id, projeto_nome: a.projeto_nome,
          pctPorMes: Object.fromEntries(MESES_1_12.map((m) => [m, 0])),
        });
      }
      mapa.get(a.projeto_id).pctPorMes[mes] = a.pct;
    });
  });
  return [...mapa.values()];
}

// Só leitura — mostra em quais projetos e meses o colaborador está alocado, sem os controles
// de edição do PopupAlocacaoColaborador (que só abre pelo ícone de lápis).
function ResumoColaboradorModal({ C, colaborador, ano, onClose, onEditar }) {
  const linhas = useMemo(() => linhasIniciais(colaborador), [colaborador]);
  const totalPorMes = useMemo(
    () => Object.fromEntries(MESES_1_12.map((mes) => [mes, linhas.reduce((acc, l) => acc + (Number(l.pctPorMes[mes]) || 0), 0)])),
    [linhas]
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 900, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Briefcase size={16} color={C.brand} />
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Resumo de {colaborador.ocupante || colaborador.cargo || "colaborador"}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{colaborador.cargo}{colaborador.ocupante ? ` · ${colaborador.ocupante}` : " · vaga em aberto"} · {ano}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        {linhas.length === 0 ? (
          <p style={{ fontSize: 13, color: C.muted, margin: "20px 0" }}>Nenhuma alocação lançada para {ano}.</p>
        ) : (
          <div style={{ overflowX: "auto", margin: "16px 0" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "4px 8px", color: C.muted, fontWeight: 700, minWidth: 220 }}>Projeto</th>
                  {MESES_ABREV.map((m) => <th key={m} style={{ padding: "4px 6px", color: C.muted, fontWeight: 700, minWidth: 56 }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.projeto_id}>
                    <td style={{ padding: "6px 8px", fontWeight: 600, color: C.text }}>{l.projeto_nome}</td>
                    {MESES_1_12.map((mes) => {
                      const pct = Number(l.pctPorMes[mes]) || 0;
                      const cor = corCelula(C, pct);
                      return (
                        <td key={mes} style={{ padding: "3px 4px", textAlign: "center" }}>
                          <div style={{ padding: "6px 2px", borderRadius: 6, background: cor.bg, color: cor.fg, fontWeight: 700 }}>
                            {pct > 0 ? `${pct}%` : "—"}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: "6px 8px", fontWeight: 700, color: C.text, borderTop: `1px solid ${C.border}` }}>Total alocado</td>
                  {MESES_1_12.map((mes) => {
                    const total = totalPorMes[mes];
                    const cor = corCelula(C, total);
                    return (
                      <td key={mes} style={{ padding: "6px 4px", textAlign: "center", fontWeight: 700, color: cor.fg, borderTop: `1px solid ${C.border}` }}>
                        {total}%
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>
            Fechar
          </button>
          <button
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            onClick={onEditar}
          >
            <Pencil size={14} /> Editar alocação
          </button>
        </div>
      </div>
    </div>
  );
}

function PopupAlocacaoColaborador({ C, colaborador, ano, projetosAtivos, onClose, onSalvo }) {
  const [linhas, setLinhas] = useState(() => linhasIniciais(colaborador));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const totalPorMes = useMemo(
    () => Object.fromEntries(MESES_1_12.map((mes) => [mes, linhas.reduce((acc, l) => acc + (Number(l.pctPorMes[mes]) || 0), 0)])),
    [linhas]
  );
  const mesesExcedidos = MESES_1_12.filter((mes) => totalPorMes[mes] > 100);
  const excedeu = mesesExcedidos.length > 0;

  function adicionarLinha() {
    setLinhas((ls) => [...ls, { projeto_id: "", projeto_nome: "", pctPorMes: Object.fromEntries(MESES_1_12.map((m) => [m, 0])) }]);
  }
  function removerLinha(idx) {
    setLinhas((ls) => ls.filter((_, i) => i !== idx));
  }
  function alterarProjeto(idx, projetoId) {
    const projeto = projetosAtivos.find((p) => p.id === projetoId);
    setLinhas((ls) => ls.map((l, i) => (i === idx ? { ...l, projeto_id: projetoId, projeto_nome: projeto?.nome || "" } : l)));
  }
  function alterarPct(idx, mes, pct) {
    setLinhas((ls) => ls.map((l, i) => (i === idx ? { ...l, pctPorMes: { ...l.pctPorMes, [mes]: pct } } : l)));
  }

  async function salvar() {
    if (excedeu) return;
    setSalvando(true);
    setErro("");
    try {
      const novasAlocacoesPorMes = {};
      for (const mes of MESES_1_12) {
        const alocacoesDoMes = linhas
          .filter((l) => l.projeto_id && Number(l.pctPorMes[mes]) > 0)
          .map((l) => ({ projeto_id: l.projeto_id, pct: Number(l.pctPorMes[mes]) }));
        novasAlocacoesPorMes[mes] = await putAlocacaoPessoal(colaborador.id, mes, ano, alocacoesDoMes);
      }
      onSalvo(colaborador.id, novasAlocacoesPorMes);
      onClose();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const opcoesProjeto = projetosAtivos.map((p) => ({ value: p.id, label: p.nome }));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 980, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Alocação de {colaborador.ocupante || colaborador.cargo || "colaborador"}</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{ano} · todos os projetos e meses</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        {erro && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, margin: "12px 0" }}>
            {erro}
          </div>
        )}

        <div style={{ overflowX: "auto", margin: "16px 0" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 8px", color: C.muted, fontWeight: 700, minWidth: 220 }}>Projeto</th>
                {MESES_ABREV.map((m) => <th key={m} style={{ padding: "4px 6px", color: C.muted, fontWeight: 700, minWidth: 62 }}>{m}</th>)}
                <th style={{ padding: "4px 6px" }} />
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, idx) => (
                <tr key={idx}>
                  <td style={{ padding: "4px 8px" }}>
                    <ComboBoxFiltravel C={C} value={l.projeto_id} onChange={(v) => alterarProjeto(idx, v)} options={opcoesProjeto} placeholder="Selecione o projeto" />
                  </td>
                  {MESES_1_12.map((mes) => (
                    <td key={mes} style={{ padding: "2px 4px", textAlign: "center" }}>
                      <input
                        type="number" min={0} max={100} value={l.pctPorMes[mes]}
                        onChange={(e) => alterarPct(idx, mes, e.target.value)}
                        style={{ width: 54, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 4px", fontSize: 12, color: C.text, fontFamily: "inherit", outline: "none", textAlign: "right" }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: "2px 4px", textAlign: "center" }}>
                    <button onClick={() => removerLinha(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, display: "inline-flex" }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: "6px 8px", fontWeight: 700, color: C.text }}>Total alocado</td>
                {MESES_1_12.map((mes) => {
                  const total = totalPorMes[mes];
                  const cor = total > 100 ? C.danger : total === 100 ? C.success : C.muted;
                  return <td key={mes} style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700, color: cor }}>{total}%</td>;
                })}
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        {projetosAtivos.length === 0 ? (
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 12px" }}>Nenhum projeto ativo cadastrado — clique em "Gerenciar Projetos" na tela principal.</p>
        ) : (
          <button onClick={adicionarLinha} style={{ ...btnStyle(C.brand, true), marginBottom: 12 }}>
            <Plus size={14} /> Adicionar projeto
          </button>
        )}

        {excedeu && (
          <p style={{ fontSize: 12, color: C.danger, margin: "0 0 12px", fontWeight: 600 }}>
            Não é possível salvar acima de 100% em {mesesExcedidos.length === 1 ? "um mês" : "vários meses"}: {mesesExcedidos.map((m) => MESES_ABREV[m - 1]).join(", ")}.
          </p>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>
            Cancelar
          </button>
          <button
            style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: excedeu || salvando ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: excedeu || salvando ? 0.5 : 1 }}
            onClick={salvar}
            disabled={excedeu || salvando}
          >
            {salvando ? "Salvando..." : "Salvar alocação"}
          </button>
        </div>
      </div>
    </div>
  );
}
